import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';
import { catchError, forkJoin, of } from 'rxjs';
import { UserService } from '../user/user.service';
import { User, UserType } from '../user/user.types';
import { cloneDeep } from 'lodash-es';
import { adminParentNavigation, adminTeacherNavigation, adminOrderOperatorNavigation, adminTestModuleOperatorNavigation, adminMarketingOperatorNavigation, adminSimulationStaffNavigation } from './data';
import { PaperAssignmentService } from '../paper-assignments/paper-assignment.service';
import { GroupFeedbackService } from '../group-feedback/group-feedback.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _userService = inject(UserService);
    private _paperAssignmentService = inject(PaperAssignmentService);
    private _groupFeedbackService = inject(GroupFeedbackService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private _user: User;

// -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    /**
     * Getter for navigation
     */
     get(): Observable<Navigation>
     {
        // Subscribe to navigation data
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this._user = user;
            });

        let navigation = {
            default   : [],
        };

        if (this._user?.type === UserType.parent){
            navigation = {
                default   : cloneDeep([...adminParentNavigation])
            };
        } else if (this._user?.type === UserType.teacher){
            navigation = {
                default   : cloneDeep([...adminTeacherNavigation])
            };
        } else if (this._user?.type === UserType.order_operator){
            navigation = {
                default   : cloneDeep([...adminOrderOperatorNavigation])
            };
        } else if (this._user?.type === UserType.test_module_operator){
            navigation = {
                default   : cloneDeep([...adminTestModuleOperatorNavigation])
            };
        } else if (this._user?.type === UserType.marketing_operator){
            navigation = {
                default   : cloneDeep([...adminMarketingOperatorNavigation])
            };
        } else if (this._user?.type === UserType.simulation_staff){
            navigation = {
                default   : cloneDeep([...adminSimulationStaffNavigation])
            };
        }

        // The "Diplome" and "Înscrieri" tabs stay visible regardless of their feature
        // toggles — the admin manages those from inside the tabs (the diploma rules and
        // the on/off switch live in Diplome → Reguli). The bulk send stays server-gated.
        if (this._user?.type === UserType.teacher) {
            // Only show "Corectare lucrări" to teachers who actually have papers to
            // correct (listMine already excludes weeks whose results were sent), and
            // "Feedback" to teachers with a feedback round in one of their grupe.
            forkJoin({
                papers: this._paperAssignmentService.listMine().pipe(catchError(() => of([]))),
                rounds: this._groupFeedbackService.getRounds().pipe(catchError(() => of([]))),
            }).subscribe(({ papers, rounds }) => {
                this.hideUnless(navigation.default, 'simulations-marking', (papers?.length ?? 0) > 0);
                this.hideUnless(navigation.default, 'group-feedback', (rounds?.length ?? 0) > 0);
                this._navigation.next(navigation);
            });
        } else {
            this._navigation.next(navigation);
        }

         return this._navigation.asObservable();
     }

    /** Remove a nav item by id unless the condition holds. Searches nested children too,
     *  so items grouped under a collapsable (e.g. "Utilitare") are still hidden. */
    private hideUnless(items: FuseNavigationItem[], id: string, show: boolean): void {
        if (show) {
            return;
        }
        this.removeById(items, id);
    }

    /** Removes the first item matching `id` anywhere in the tree. Returns true if removed. */
    private removeById(items: FuseNavigationItem[], id: string): boolean {
        const idx = items.findIndex((i: any) => i?.id === id);
        if (idx >= 0) {
            items.splice(idx, 1);
            return true;
        }
        for (const item of items) {
            if (item.children?.length && this.removeById(item.children, id)) {
                return true;
            }
        }
        return false;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
